import { CurrencyFormatter } from '@mixins/currency-formatter-mixin';
import Builder from '@models/builder';
import StaticModel from '@models/static-model';
import { getProperty, setProperty } from 'dot-prop';
import { camelCase } from 'lodash';

import ApiClient from '@/api-client';
import ModelNotFound from '@/exceptions/ModelNotFoundError';
/* JavaScript */
import { deepMerge } from '@/libs/deep-merge';

/**
 * Model class documentation.
 *
 * @class
 * @extends StaticModel
 */
export default class Model extends StaticModel {
  static apiMetaKey = Symbol('meta');

  static builderKey = Symbol('builder');

    static allowsEdit = false;

    static formFieldsConfig = {}

    static allowedFieldEdits = [];

    static className = 'Model';

    index = false;

    yayPay = false;

    sendRecentlyViewed = false;

    #wasRecentlyCreated = false;

    constructor(data, tmp = false) {
        super();
        this.id = null;
        this.option_dropdown = [];
        this.tmp = tmp;

        this[Model.builderKey] = new Builder();
        const resourcePrefix = this.resourcePrefix() ? `/${this.resourcePrefix()}` : '';

        this[Model.builderKey].url = `${resourcePrefix}/${this.resource()}`;

        this.setAttributes(data);
    }

    hasId() {
        return !!this.id;
    }

    validate(data) {
        return ApiClient.post(`${process.env.MIX_APP_URL}/api/validate/${camelCase(this.constructor.className)}`, {
            ...data,
        });
    }

    setBuilderUrl({ url }) {
        this[Model.builderKey].url = url;

        return this;
    }

    setBuilderIncludes({ includes }) {
        this[Model.builderKey].includes = includes;
        return this;
    }

    getApiUrl(withParams = false) {
        return ApiClient.getUri({
            url: `${this[Model.builderKey].baseUrl}${this[Model.builderKey].url}`,
            params: withParams ? this[Model.builderKey].getParams().params : {},
        });
    }

    api() {
        return ApiClient;
    }

    static api() {
        return ApiClient;
    }

    getBuilder() {
        return this[Model.builderKey];
    }

    getBuilderUrl() {
        return this[Model.builderKey].url;
    }

    /**
   * Set the attributes of the model.
   *
   * @param data
   * @returns {Model}
   */
    setAttributes(data) {
        Object.assign(this, data);

        this.applyRelations();

        Object.keys(this).forEach((key) => {
            this[key] = this.applyFieldCast(this, key);
        });
        return this;
    }

    updateAttributes(data) {
        deepMerge(this, data);

        this.applyRelations();

        Object.keys(this).forEach((key) => {
            this[key] = this.applyFieldCast(this, key);
        });

        return this;
    }

    applyFieldCast(obj, field) {
        if (obj.propCasts[field] && obj[field] !== null && obj[field] !== undefined) {
            if (Array.isArray(obj.propCasts[field])) {
                return obj.propCasts[field][0](obj[field], ...obj.propCasts[field].slice(1));
            }

            if (obj[field]) {
                return obj.propCasts[field](obj[field]);
            }
        }
        return obj[field];
    }

    resource() {
        if (!this.resource.className !== 'Model') {
            throw new Error(`The resource() method must be implemented for model ${this.constructor.className}`);
        }
    }

    resourcePrefix() {
        return '';
    }

    relations() {
        return { };
    }

    get formFieldCasts() {
        return { };
    }

    get propCasts() {
        return { };
    }

    /**
     * Orders the Model records based on the specified field and direction.
     *
     * @param {string} field - The field to order by.
     * @param {string} [direction='asc'] - The direction of the ordering. Defaults to 'asc'.
     * @returns {Model} - The updated Model instance with the ordering applied.
     */
    orderBy(field, direction = 'asc') {
        this[Model.builderKey].orderBy(field, direction);

        return this;
    }

    /**
     * Executes a callback method when a certain condition is met.
     *
     * @param {boolean} condition - The condition to be checked.
     * @param {function} callback - The callback method to be executed when the condition is met.
     * @returns {Model} - The current instance of the Model.
     */
    when(condition, callback) {
        if (!!condition && typeof callback === 'function') {
            callback(this);
        }

        return this;
    }

    /**
   * Sets a WHERE condition for the query.
   *
   * @param {string} field - The field to apply the condition on.
   * @param {any} value - The value to compare with.
   * @returns {Model} - The updated model instance with the WHERE condition applied.
   */
    where(field, value) {
        this[Model.builderKey].where(field, value);

        return this;
    }

    /**
   * Filters the query based on multiple values of a field.
   *
   * @param {string} field - The field to filter on.
   * @param {Array} values - The array of values to filter on.
   *
   * @returns {this} - Returns the modified query object.
   */
    whereIn(field, values) {
        this[Model.builderKey].whereIn(field, values);

        return this;
    }

    /**
   * Set the 'is-active' filter to true.
   *
   * @returns {this} - The current object after setting the filter.
   */
    isActive() {
        this.where('is_active', true);

        return this;
    }

    search(value) {
        this[Model.builderKey].where('search', value);

        return this;
    }

    limit(limit) {
        this[Model.builderKey].limit = limit;

        return this;
    }

    list() {
        this[Model.builderKey].layout = 'list';

        return this;
    }

    /**
     * @param {string} format
     * @returns {Model}
     */
    responseFormat(format) {
        this[Model.builderKey].layout = format;

        return this;
    }

    /**
     * @param {string} key
     * @param {string} value
     * @returns {Model}
     */
    query(key, value) {
        this[Model.builderKey].query(key, value);

        return this;
    }

    currency(prop, decimals = 2) {
        return CurrencyFormatter.formatWithSymbol(this[prop], decimals);
    }

    clone() {
        return this.applyInstance(this);
    }

    wasRecentlyCreated() {
        return this.wasRecentlyCreated;
    }

    applyInstance(data, model = this.constructor) {
        // eslint-disable-next-line new-cap
        const newObject = new model();
        newObject.setBuilderUrl(this[Model.builderKey]);

        newObject.setAttributes(data);

        return new Proxy(newObject, this.proxy());
    }

    proxy() {
        return {
            get(target, property) {
                // `target.constructor` is the actual ES6 class. Don't wrap it
                // in a function — callers (e.g. Form.applyFieldCast doing
                // `new item.constructor({...item})`) need the real class so
                // `new` can construct it. Wrapping in `execute` would make
                // `new wrapper(args)` succeed at the wrapper level but fail
                // when the wrapper internally calls `target.constructor(args)`
                // without `new`, which throws "class constructors must be
                // invoked with 'new'".
                if (property === 'constructor') {
                    return target.constructor;
                }
                if (typeof target[property] === 'function') {
                    return function execute(...args) {
                        return target[property](...args);
                    };
                }

                return target[property];
            },
        };
    }

    applyInstanceCollection(data, model = this.constructor, meta) {
        let collection = data.data || data;
        collection = Array.isArray(collection) ? collection : [collection];

        collection = collection.map((c) => this.applyInstance(c, model));

        if (meta !== undefined) {
            collection[Model.apiMetaKey] = meta;
        }

        return collection;
    }

    applyRelations() {
        const relations = this.relations();

        Object.keys(relations).forEach((key) => {
            const relation = getProperty(this, key);

            if (relation) {
                if (Array.isArray(relation.data) || Array.isArray(relation)) {
                    const collection = this.applyInstanceCollection(
                        relation,
                        relations[key],
                    );

                    collection.forEach((item) => {
                        // eslint-disable-next-line no-param-reassign
                        item.getBuilder().url = `${item.getBuilder().url}/${this.id}/${item.resource()}`;
                    });

                    if (relation.data !== undefined) {
                        relation.data = collection;
                    } else {
                        setProperty(this, key, collection);
                    }
                } else {
                    setProperty(
                        this,
                        key,
                        this.applyInstance(relation, relations[key]),
                    );
                }
            }
        });
    }

    static getCollectionMeta(collection) {
        return collection[Model.apiMetaKey] || {};
    }

    with(...include) {
        if (include.length === 0) {
            throw new Error('The with() method takes a minimum of one argument.');
        }
        if (include.length === 1 && Array.isArray(include[0])) {
            throw new Error('The with() method does not accept an array as argument.');
        }
        if (include.length > 0) {
            this[Model.builderKey].includes.push(...include);
        }

        return this;
    }

    for(...args) {
        if (args.length === 0) {
            throw new Error('The for() method takes a minimum of one argument.');
        }

        let url = '';

        args.forEach((object) => {
            if (!(object instanceof Model)) {
                throw new Error('The object referenced on for() method is not a valid Model.');
            }

            if (!object.id) {
                throw new Error('The object referenced on for() method has an invalid id.');
            }

            url += `${object.getBuilderUrl()}/${object.id}`;
        });

        url += `/${this.resource()}`;

        this[Model.builderKey].url = url;

        return this;
    }

    hasMany(model) {
        // eslint-disable-next-line new-cap
        const instance = new model();

        instance.for(this);

        return instance;
    }

    load(...relations) {
        // foreach relations che
        this.with(...relations);

        return this.refresh();
    }

    /**
     * Refreshes the model instance by fetching the latest data from the server.
     *
     * @returns {Promise<Model>} A promise that resolves to the updated model instance.
     */
    async refresh() {
        const response = this.find(this.id);

        return this.setAttributes(response.data);
    }

    async find(id) {
        if (this.sendRecentlyViewed) {
            this.setRecentlyViewed(id);
        }
        const url = `${this[Model.builderKey].baseUrl}${this[Model.builderKey].url}/${id}`;

        try {
            const response = await ApiClient.get(url,
                this[Model.builderKey].getParams());

            const instance = this.applyInstance(response.data, this.constructor);
            instance.setBuilderIncludes(this[Model.builderKey]);

            return instance;
        } catch (e) {
            if (e.statusCode === 404) {
                throw new ModelNotFound(`The ${this.constructor.name} with id ${id} was not found.`);
            }

            throw e;
        }
    }

    async get() {
        const response = await ApiClient.get(this.getApiUrl(), this[Model.builderKey].getParams());

        return this.applyInstanceCollection(response.data.data, this.constructor, response.data.meta);
    }

    async count() {
        const config = { ...this[Model.builderKey].getParams() };
        config.params.per_page = 1;

        const response = await ApiClient.get(this.getApiUrl(), config);

        const collection = this.applyInstanceCollection(response.data.data, this.constructor, response.data.meta);

        return Model.getCollectionMeta(collection);
    }

    async first() {
        const response = await ApiClient.get(this.getApiUrl(), this[Model.builderKey].getParams());

        if (Array.isArray(response.data.data)) {
            const item = response.data.data.shift();
            if (item) {
                return this.applyInstance(item, this.constructor);
            }
            return null;
        }

        return this.applyInstance(response.data || {}, this.constructor);
    }

    save(data) {
        if (this.id) {
            return this.update(data || this);
        }

        return this.create(data || this);
    }

    async create(data) {
        const response = await ApiClient.post(this.getApiUrl(), data, this[Model.builderKey].getParams());

        const item = this.applyInstance(response.data, this.constructor);

        item.wasRecentlyCreated = true;

        return item;
    }

    async update(data) {
        const response = await ApiClient.patch(`${this.getApiUrl()}/${this.id}`, data, this[Model.builderKey].getParams());

        return this.updateAttributes(response.data);
    }

    async delete(data) {
        await ApiClient.delete(`${this.getApiUrl()}/${this.id}`, {
            data,
        });
    }

    request(method, action, data) {
        return ApiClient.request({
            method,
            url: `${this.getApiUrl()}/${this.id}/${action}`,
            data,
            ...this[Model.builderKey].getParams(),
        });
    }

    async post(action, data) {
        const response = await this.request('post', action, data);

        return this.applyInstance(response.data, this.constructor);
    }

    getIndexUrl() {
        return `api/index/${this.constructor.className}/${this.id}`;
    }

    hasIndex() {
        return this.index;
    }

    getDuplicateUrl() {
        return `api/duplicate/${this.constructor.className}/${this.id}`;
    }

    hasDuplicate() {
        return typeof this.duplicate === 'function';
    }

    getClassName() {
        return this.constructor.className;
    }

    hasYayPay() {
        return this.yayPay;
    }

    canEdit() {
        return this.constructor.allowsEdit;
    }

    getYayPayUrl() {
        return `api/yaypay/${this.constructor.className}/${this.id}`;
    }

    async setRecentlyViewed(id) {
        const response = await this.api().post('/api/recently-viewed', {
            model: this.constructor.className,
            id,
        });

        return this.applyInstance(response.data, this.constructor);
    }

    returnFieldConfig() {
        return Object.keys(this)
            .filter((key) => this.constructor.allowedFieldEdits.includes(key) || this.constructor.allowedFieldEdits.length === 0)
            .map((key) => {
                const field = this[key];

                let type = typeof field;

                if (field == null) {
                    type = 'string';
                }

                const formConfig = this.constructor.formFieldsConfig[key];

                return {
                    name: key,
                    label: key,
                    type,
                    ...formConfig,
                };
            });
    }
}
